import { TableOrderingContext } from '../types/tableOrdering';

/**
 * Session storage keys with 't_' prefix for table ordering
 */
const SESSION_KEYS = {
  HOTEL_ID: 't_hotel_id',
  RESTAURANT_ID: 't_restaurant_id',
  TABLE_ID: 't_table_id',
  API_KEY: 't_api_key',
  CODE: 't_code',
  SOURCE: 't_source'
};

// Read by tableOrderingService when a call has no context to hand.
const LOCAL_API_KEY = 'table_ordering_api_key';
const LOCAL_CODE = 'table_ordering_code';

const setOrRemove = (storage: Storage, key: string, value?: string) => {
  if (value) storage.setItem(key, value);
  else storage.removeItem(key);
};

/**
 * Save table ordering context to session storage
 */
export const saveContextToSession = (context: TableOrderingContext): void => {
  try {
    sessionStorage.setItem(SESSION_KEYS.HOTEL_ID, context.hotelId);
    sessionStorage.setItem(SESSION_KEYS.RESTAURANT_ID, context.restaurantId);
    sessionStorage.setItem(SESSION_KEYS.TABLE_ID, context.tableId);
    setOrRemove(sessionStorage, SESSION_KEYS.API_KEY, context.apiKey);
    setOrRemove(sessionStorage, SESSION_KEYS.CODE, context.code);
    sessionStorage.setItem(SESSION_KEYS.SOURCE, context.source);

    // A new scan replaces both credentials, so a key or code from an
    // earlier table/hotel never leaks into this one.
    setOrRemove(localStorage, LOCAL_API_KEY, context.apiKey);
    setOrRemove(localStorage, LOCAL_CODE, context.code);
  } catch (error) {
    console.error('❌ Failed to save table context to session storage:', error);
  }
};

/**
 * Load table ordering context from session storage
 */
export const loadContextFromSession = (): TableOrderingContext | null => {
  try {
    const hotelId = sessionStorage.getItem(SESSION_KEYS.HOTEL_ID);
    const restaurantId = sessionStorage.getItem(SESSION_KEYS.RESTAURANT_ID);
    const tableId = sessionStorage.getItem(SESSION_KEYS.TABLE_ID);
    const apiKey = sessionStorage.getItem(SESSION_KEYS.API_KEY) || undefined;
    const code = sessionStorage.getItem(SESSION_KEYS.CODE) || undefined;
    const source = sessionStorage.getItem(SESSION_KEYS.SOURCE) as 'qr' | 'direct' || 'direct';

    if (!hotelId || !restaurantId || !tableId || (!apiKey && !code)) {
      return null;
    }

    return { hotelId, restaurantId, tableId, apiKey, code, source };
  } catch (error) {
    console.warn('Failed to load table context from session storage:', error);
    return null;
  }
};

/**
 * Clear table ordering context from session storage
 */
export const clearContextFromSession = (): void => {
  try {
    Object.values(SESSION_KEYS).forEach((key) => sessionStorage.removeItem(key));
    localStorage.removeItem(LOCAL_API_KEY);
    localStorage.removeItem(LOCAL_CODE);
  } catch (error) {
    console.warn('Failed to clear table context from session storage:', error);
  }
};

/**
 * Parse table ordering context from URL parameters with session fallback
 * URL parameters always take priority and overwrite session storage.
 *
 * A link needs hotel, restaurant and table plus one credential: the
 * booking-engine `api_key`, or the table's QR `code` (what QR cards
 * printed from the POS carry).
 */
export const parseContextFromURL = (searchParams: URLSearchParams): TableOrderingContext | null => {
  const hotelId = searchParams.get('hotel');
  const restaurantId = searchParams.get('restaurant');
  const tableId = searchParams.get('table');
  const apiKey = searchParams.get('api_key') || undefined;
  const code = searchParams.get('code')?.trim() || undefined;
  const source = searchParams.get('source') as 'qr' | 'direct' || 'direct';

  const hasAnyUrlParams = hotelId || restaurantId || tableId || apiKey || code || searchParams.get('source');

  if (hasAnyUrlParams) {
    if (hotelId && restaurantId && tableId && (apiKey || code)) {
      const context: TableOrderingContext = { hotelId, restaurantId, tableId, apiKey, code, source };
      saveContextToSession(context);
      return context;
    }
    // Incomplete link - clear session to avoid ordering against an old table
    console.warn('⚠️ Incomplete table link', {
      hotelId: !!hotelId, restaurantId: !!restaurantId, tableId: !!tableId, credential: !!(apiKey || code)
    });
    clearContextFromSession();
    return null;
  }

  // No URL parameters provided, try to load from session storage
  return loadContextFromSession();
};
